import { createSlice } from '@reduxjs/toolkit';

import { requestFailedMatcher } from '../request-failed/reducer';
import {
  type IAccountDeleteResponse,
  type IAccountState,
  type IAddressCreate,
  type IAddressDelete,
  type IAddressesListResponse,
  type IAddressPatch,
  type ICouponsListResponse,
  type IDataExportResponse,
  type ILogin,
  type ILoginLinkChallengeResponse,
  type ILoginLinkRequest,
  type ILoginLinkRequestResponse,
  type ILoginResponseAction,
  type ILogoutResponse,
  type IOrderCancel,
  type IOrderCancelResponse,
  type IOrderGet,
  type IOrderGetResponse,
  type IOrderPaymentRetry,
  type IOrderPaymentRetryResponse,
  type IOrdersList,
  type IOrdersListResponse,
  type IPasswordRemove,
  type IPasswordSet,
  type IPasswordSetResponse,
  type IProfileGetResponse,
  type IReferralCodeResponse,
  type ISessionRevoke,
  type ISessionRevokeResponse,
  type ISessionsListResponse,
  type ISettingsUpdate,
  type ISettingsUpdateResponse,
} from './types';

export const initialState: IAccountState = {
  isSignedIn: false,
  profile: null,
  isLoadingAuth: false,
  isLoadingOrders: false,
  isLoadingProfile: false,
  isLoadingAddresses: false,
  isLoadingPassword: false,
  isLoadingSessions: false,
  isLoadingCaptcha: false,
  loginLinkSent: false,
  loginFailed: false,
  loginToken: null,
  captchaA: null,
  captchaB: null,
  captchaToken: null,
  captchaFailed: false,
  currentOrder: null,
  orders: [],
  ordersPage: 1,
  ordersPagesCount: 0,
  ordersLoaded: false,
  orderStatuses: [],
  addresses: [],
  addressesLoaded: false,
  addressCreateError: null,
  addressPatchError: null,
  passwordSaved: null,
  passwordError: null,
  sessions: [],
  sessionsLoaded: false,
  isRetryingPayment: false,
  paymentRetryError: false,
  paymentRetryRedirectLink: null,
  isCancellingOrder: false,
  orderCancelError: false,
  coupons: [],
  couponsLoaded: false,
  isLoadingCoupons: false,
  isSavingSettings: false,
  referralCode: null,
  referralCodeLoaded: false,
  isLoadingReferralCode: false,
  isExportingData: false,
  exportedData: null,
  isDeletingAccount: false,
  accountDeleted: false,
};

const accountSlice = createSlice({
  name: 'Account',
  initialState,
  reducers: {
    loginLinkChallengeRequest: (state): void => {
      state.isLoadingCaptcha = true;
      state.captchaA = null;
      state.captchaB = null;
      state.captchaToken = null;
    },
    loginLinkChallengeResponse: (
      state,
      action: ILoginLinkChallengeResponse,
    ): void => {
      state.isLoadingCaptcha = false;
      state.captchaA = action.payload.a;
      state.captchaB = action.payload.b;
      state.captchaToken = action.payload.token;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loginLinkRequest: (state, _action: ILoginLinkRequest): void => {
      state.isLoadingAuth = true;
      state.loginLinkSent = false;
      state.captchaFailed = false;
    },
    loginLinkRequestResponse: (
      state,
      action: ILoginLinkRequestResponse,
    ): void => {
      if (action.payload.captchaFailed) {
        state.isLoadingAuth = false;
        state.captchaFailed = true;
        return;
      }
      state.isLoadingAuth = false;
      state.loginLinkSent = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    login: (state, _action: ILogin): void => {
      state.isLoadingAuth = true;
      state.loginFailed = false;
    },
    loginResponse: (state, action: ILoginResponseAction): void => {
      state.isLoadingAuth = false;

      if (action.payload.ok) {
        if (action.payload.loginToken) {
          state.loginToken = action.payload.loginToken;
        }
        state.loginFailed = false;
      } else {
        state.loginFailed = true;
      }
    },
    logout: (state): void => {
      state.isLoadingAuth = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    logoutResponse: (state, _action: ILogoutResponse): void => {
      Object.assign(state, initialState);
    },
    profileGet: (state): void => {
      state.isLoadingProfile = true;
    },
    profileGetResponse: (state, action: IProfileGetResponse): void => {
      state.isLoadingProfile = false;

      if (action.payload.ok && action.payload.profile) {
        state.isSignedIn = true;
        state.profile = action.payload.profile;
      } else if (!action.payload.ok) {
        state.isSignedIn = false;
        state.profile = null;
      }
    },
    addressesList: (state): void => {
      state.isLoadingAddresses = true;
    },
    addressesListResponse: (state, action: IAddressesListResponse): void => {
      state.isLoadingAddresses = false;

      if (action.payload.ok && action.payload.addresses) {
        state.addresses = action.payload.addresses;
        state.addressesLoaded = true;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addressCreate: (state, _action: IAddressCreate): void => {
      state.isLoadingAddresses = true;
      state.addressCreateError = null;
    },
    addressCreateResponse: (state, action: IAddressesListResponse): void => {
      state.isLoadingAddresses = false;

      if (action.payload.ok && action.payload.addresses) {
        state.addresses = action.payload.addresses;
        state.addressCreateError = false;
      } else if (!action.payload.ok) {
        state.addressCreateError = true;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addressPatch: (state, _action: IAddressPatch): void => {
      state.isLoadingAddresses = true;
      state.addressPatchError = null;
    },
    addressPatchResponse: (state, action: IAddressesListResponse): void => {
      state.isLoadingAddresses = false;

      if (action.payload.ok && action.payload.addresses) {
        state.addresses = action.payload.addresses;
        state.addressPatchError = false;
      } else if (!action.payload.ok) {
        state.addressPatchError = true;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addressDelete: (state, _action: IAddressDelete): void => {
      state.isLoadingAddresses = true;
    },
    addressDeleteResponse: (state, action: IAddressesListResponse): void => {
      state.isLoadingAddresses = false;

      if (action.payload.ok && action.payload.addresses) {
        state.addresses = action.payload.addresses;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    orderGet: (state, _action: IOrderGet): void => {
      state.isLoadingOrders = true;
      state.currentOrder = null;
    },
    orderGetResponse: (state, action: IOrderGetResponse): void => {
      state.isLoadingOrders = false;

      if (action.payload.ok && action.payload.order) {
        state.currentOrder = action.payload.order;
      }
    },
    orderPaymentRetry: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IOrderPaymentRetry,
    ): void => {
      state.isRetryingPayment = true;
      state.paymentRetryError = false;
      state.paymentRetryRedirectLink = null;
    },
    orderPaymentRetryResponse: (
      state,
      action: IOrderPaymentRetryResponse,
    ): void => {
      state.isRetryingPayment = false;

      if (action.payload.ok && action.payload.redirectLink) {
        state.paymentRetryRedirectLink = action.payload.redirectLink;
      } else {
        state.paymentRetryError = true;
      }
    },
    orderCancel: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: IOrderCancel,
    ): void => {
      state.isCancellingOrder = true;
      state.orderCancelError = false;
    },
    orderCancelResponse: (state, action: IOrderCancelResponse): void => {
      state.isCancellingOrder = false;

      if (action.payload.ok && action.payload.order) {
        state.currentOrder = action.payload.order;
      } else {
        state.orderCancelError = true;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ordersList: (state, _action: IOrdersList): void => {
      state.isLoadingOrders = true;
    },
    ordersListResponse: (state, action: IOrdersListResponse): void => {
      state.isLoadingOrders = false;
      state.ordersLoaded = true;

      if (action.payload.ok && action.payload.orders) {
        state.orders = action.payload.orders;
        state.ordersPage = action.payload.page ?? 1;
        state.ordersPagesCount = action.payload.pagesCount ?? 0;
      }

      if (action.payload.ok && action.payload.statuses) {
        state.orderStatuses = action.payload.statuses;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    passwordSet: (state, _action: IPasswordSet): void => {
      state.isLoadingPassword = true;
      state.passwordSaved = null;
      state.passwordError = null;
    },
    passwordSetResponse: (state, action: IPasswordSetResponse): void => {
      state.isLoadingPassword = false;
      state.passwordSaved = action.payload.ok;
      state.passwordError = action.payload.error ?? null;

      if (action.payload.ok && state.profile) {
        state.profile.hasPassword = true;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    passwordRemove: (state, _action: IPasswordRemove): void => {
      state.isLoadingPassword = true;
      state.passwordSaved = null;
      state.passwordError = null;
    },
    passwordRemoveResponse: (state, action: IPasswordSetResponse): void => {
      state.isLoadingPassword = false;
      state.passwordSaved = action.payload.ok ? true : null;
      state.passwordError = action.payload.error ?? null;

      if (action.payload.ok && state.profile) {
        state.profile.hasPassword = false;
      }
    },
    clearLoginToken: (state): void => {
      state.loginToken = null;
    },
    sessionsList: (state): void => {
      state.isLoadingSessions = true;
    },
    sessionsListResponse: (state, action: ISessionsListResponse): void => {
      state.isLoadingSessions = false;
      state.sessionsLoaded = true;
      if (action.payload.ok && action.payload.sessions) {
        state.sessions = action.payload.sessions;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sessionRevoke: (state, _action: ISessionRevoke): void => {
      state.isLoadingSessions = true;
    },
    sessionRevokeResponse: (state, action: ISessionRevokeResponse): void => {
      state.isLoadingSessions = false;
      if (action.payload.ok && action.payload.sessions) {
        state.sessions = action.payload.sessions;
      }
    },
    couponsList: (state): void => {
      state.isLoadingCoupons = true;
    },
    couponsListResponse: (state, action: ICouponsListResponse): void => {
      state.isLoadingCoupons = false;
      state.couponsLoaded = true;

      if (action.payload.ok && action.payload.coupons) {
        state.coupons = action.payload.coupons;
      }
    },
    referralCodeGet: (state): void => {
      state.isLoadingReferralCode = true;
    },
    referralCodeGetResponse: (state, action: IReferralCodeResponse): void => {
      state.isLoadingReferralCode = false;
      state.referralCodeLoaded = true;

      if (action.payload.ok && action.payload.referralCode !== undefined) {
        state.referralCode = action.payload.referralCode;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    settingsUpdate: (state, _action: ISettingsUpdate): void => {
      state.isSavingSettings = true;
    },
    settingsUpdateResponse: (state, action: ISettingsUpdateResponse): void => {
      state.isSavingSettings = false;

      if (action.payload.ok && action.payload.profile) {
        state.profile = action.payload.profile;
      }
    },
    dataExport: (state): void => {
      state.isExportingData = true;
    },
    dataExportResponse: (state, action: IDataExportResponse): void => {
      state.isExportingData = false;

      if (action.payload.ok && action.payload.data) {
        state.exportedData = action.payload.data;
      }
    },
    dataExportClear: (state): void => {
      state.exportedData = null;
    },
    accountDelete: (state): void => {
      state.isDeletingAccount = true;
    },
    accountDeleteResponse: (state, action: IAccountDeleteResponse): void => {
      state.isDeletingAccount = false;
      state.accountDeleted = action.payload.ok;
    },
  },
  extraReducers: (builder): void => {
    builder.addMatcher(requestFailedMatcher('Account'), (state): void => {
      state.isLoadingAuth = false;
      state.isLoadingOrders = false;
      state.isLoadingProfile = false;
      state.isLoadingAddresses = false;
      state.isLoadingPassword = false;
      state.isLoadingSessions = false;
      state.isLoadingCaptcha = false;
      state.isLoadingCoupons = false;
      state.isLoadingReferralCode = false;

      if (state.isRetryingPayment) {
        state.isRetryingPayment = false;
        state.paymentRetryError = true;
      }
    });
  },
});

export const {
  loginLinkChallengeRequest,
  loginLinkChallengeResponse,
  loginLinkRequest,
  loginLinkRequestResponse,
  login,
  loginResponse,
  logout,
  logoutResponse,
  profileGet,
  profileGetResponse,
  addressesList,
  addressesListResponse,
  addressCreate,
  addressCreateResponse,
  addressPatch,
  addressPatchResponse,
  addressDelete,
  addressDeleteResponse,
  orderGet,
  orderGetResponse,
  orderPaymentRetry,
  orderPaymentRetryResponse,
  orderCancel,
  orderCancelResponse,
  ordersList,
  ordersListResponse,
  passwordSet,
  passwordSetResponse,
  passwordRemove,
  passwordRemoveResponse,
  clearLoginToken,
  sessionsList,
  sessionsListResponse,
  sessionRevoke,
  sessionRevokeResponse,
  couponsList,
  couponsListResponse,
  referralCodeGet,
  referralCodeGetResponse,
  settingsUpdate,
  settingsUpdateResponse,
  dataExport,
  dataExportResponse,
  dataExportClear,
  accountDelete,
  accountDeleteResponse,
} = accountSlice.actions;

export default accountSlice.reducer;
